import { ScrollText } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState, type ReactNode } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Calendar, Download, Info, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// GSTR-3B has NO data source in this system: no endpoint computes a 3B summary and
// filing it happens on the GST portal. Every cell below is therefore rendered as an
// unset em dash rather than `₹0`, so the page cannot be read as a computed nil
// liability. The design of the form (section numbers, sub-items, column order) is
// kept exactly as the statutory return presents it.

/** Right-aligned, Indian-grouped figure cell. `—` when the cell carries no value. */
const FIGURE = 'px-5 py-2.5 text-right font-mono tabular-nums text-data-md';
const SECTION_TH = 'px-5 py-2.5 text-right text-label-caps uppercase text-on-surface-variant';
const LABEL_TH = 'px-5 py-2.5 text-left text-label-caps uppercase text-on-surface-variant';

const UNSET = '—';

function ReturnSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-headline-sm font-semibold text-on-surface">{title}</h2>
      {children}
    </section>
  );
}

function TableShell({ children, wide = true }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className={`overflow-hidden rounded-card border border-border bg-card shadow-xs ${wide ? 'overflow-x-auto' : ''}`}>
      {children}
    </div>
  );
}

/** A table row group heading (e.g. "(A) ITC Available") — a hairline band, not a colour block. */
function BandRow({ span, children }: { span: number; children: ReactNode }) {
  return (
    <tr className="border-y border-border bg-muted">
      <td colSpan={span} className="px-5 py-2 text-label-caps uppercase tracking-wider text-on-surface-variant">{children}</td>
    </tr>
  );
}

export function Gstr3bReportComponent() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('');

  const notice = (
    <div className="rounded-card border border-warning/25 bg-warning/10 p-4">
      <p className="text-body-md font-semibold text-warning-text">Blank return template — not computed from your books.</p>
      <p className="mt-1 text-body-sm text-warning-text">
        Every cell shows “—” because this screen has no data source: no endpoint in this system builds a GSTR-3B
        summary, and filing it is a statutory act performed on the GST portal, which this app does not connect to.
        An em dash means “nothing has been supplied here”, never “nil”. The outward figures that do exist live on the
        GSTR-1 report and the Tax Summary. Do not treat this template as the company’s liability for any period.
      </p>
    </div>
  );

  const handleDownloadPDF = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Top Header Bar */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <PageHeader icon={ScrollText} eyebrow="GST & Compliance" title="GSTR-3B Summary" description="Summary return layout: outward tax, input tax credit and tax payable." />
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 w-4" /> Back</Button>
          <Button onClick={handleDownloadPDF} variant="outline" className="flex items-center gap-2">
            <Download className="w-4 w-4" />
            Print / save PDF
          </Button>
        </div>
      </div>

      {notice}

      {/* Reporting Period Selector */}
      <div className="flex flex-col space-y-1.5">
        <label htmlFor="gstr3b-period" className="text-label-sm uppercase tracking-wider text-on-surface-variant">Reporting Period</label>
        <div className="relative w-52">
          <select
            id="gstr3b-period"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 pr-9 text-body-md font-medium text-on-surface shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="" disabled>Select a period</option>
            <option value="Jul 2026">Jul 2026</option>
            <option value="Jun 2026">Jun 2026</option>
            <option value="May 2026">May 2026</option>
            <option value="Q1 FY 2026-27">Q1 FY 2026-27</option>
          </select>
          <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-subtle" />
        </div>
      </div>

      {/* Summary */}
      <ReturnSection title="Summary">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-card border border-border bg-card p-4 shadow-xs">
            <div className="mb-1 flex items-center gap-1.5 text-label-sm uppercase tracking-wider text-on-surface-variant">
              <span>Output tax</span>
              <Info className="h-3.5 w-3.5 text-on-surface-subtle" />
            </div>
            <p className="font-mono tabular-nums text-data-lg font-semibold text-on-surface">{UNSET}</p>
          </div>

          <div className="rounded-card border border-border bg-card p-4 shadow-xs">
            <div className="mb-1 flex items-center gap-1.5 text-label-sm uppercase tracking-wider text-on-surface-variant">
              <span>Net ITC available</span>
              <Info className="h-3.5 w-3.5 text-on-surface-subtle" />
            </div>
            <p className="font-mono tabular-nums text-data-lg font-semibold text-on-surface">{UNSET}</p>
          </div>
        </div>
      </ReturnSection>

      {/* Tax Breakdown */}
      <ReturnSection title="Tax Breakdown">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {['IGST', 'CGST', 'SGST / UTGST', 'CESS'].map((tax) => (
            <div key={tax} className="rounded-card border border-border bg-card p-4 shadow-xs">
              <p className="mb-1 text-label-sm uppercase tracking-wider text-on-surface-variant">{tax}</p>
              <p className="font-mono tabular-nums text-data-lg font-semibold text-on-surface">{UNSET}</p>
            </div>
          ))}
        </div>
      </ReturnSection>

      {/* ── 3.1 Details of outward supplies & inward supplies liable to reverse charge ── */}
      <ReturnSection title="3.1 Details of outward supplies & inward supplies liable to reverse charge">
        <TableShell>
          <table className="w-full min-w-[700px] text-body-md text-left border-collapse">
            <thead className="border-b border-border bg-muted">
              <tr>
                <th className={LABEL_TH}>Nature of Supplies</th>
                <th className={SECTION_TH}>Total Taxable Value</th>
                <th className={SECTION_TH}>Integrated Tax</th>
                <th className={SECTION_TH}>Central Tax</th>
                <th className={SECTION_TH}>State/UT Tax</th>
                <th className={SECTION_TH}>CESS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                { nature: '(a) Outward taxable supplies (other than zero rated, nil rated and exempted)', cols: 5 },
                { nature: '(b) Outward taxable supplies (zero rated)', cols: 3 },
                { nature: '(c) Other outward supplies (Nil rated, exempted)', cols: 1 },
                { nature: '(d) Inward supplies (liable to reverse charge)', cols: 5 },
                { nature: '(e) Non-GST outward supplies', cols: 0 },
              ].map((row) => (
                <tr key={row.nature} className="hover:bg-muted/50">
                  <td className="px-5 py-2.5 font-medium text-on-surface">{row.nature}</td>
                  {Array.from({ length: 5 }, (_, i) => (
                    <td key={i} className={`${FIGURE} ${i < row.cols ? 'text-on-surface' : 'text-on-surface-subtle'}`}>
                      {i < row.cols ? UNSET : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      </ReturnSection>

      {/* ── 3.1.1 Details of supplies notified under sub-section (5) of section 9 ── */}
      <ReturnSection title="3.1.1 Details of supplies notified under sub-section (5) of section 9 of Central Tax Act">
        <TableShell>
          <table className="w-full min-w-[700px] text-body-md text-left border-collapse">
            <thead className="border-b border-border bg-muted">
              <tr>
                <th className={LABEL_TH}>Description</th>
                <th className={SECTION_TH}>Total Taxable Value</th>
                <th className={SECTION_TH}>Integrated Tax</th>
                <th className={SECTION_TH}>Central Tax</th>
                <th className={SECTION_TH}>State/UT Tax</th>
                <th className={SECTION_TH}>CESS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                '(i) Taxable supplies on which electronic commerce operator pays tax u/s 9(5) [to be furnished by electronic commerce operator]',
                '(ii) Taxable supplies made by registered person through electronic commerce operator, on which electronic commerce operator is required to pay tax u/s 9(5) [to be furnished by registered person making supplies through electronic commerce operator]',
              ].map((desc) => (
                <tr key={desc} className="hover:bg-muted/50">
                  <td className="px-5 py-2.5 text-on-surface-variant">{desc}</td>
                  {Array.from({ length: 5 }, (_, i) => <td key={i} className={`${FIGURE} text-on-surface-subtle`}>{UNSET}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      </ReturnSection>

      {/* ── 3.2 Inter-State Supplies ── */}
      <ReturnSection title="3.2 Of the supplies shown in 3.1 (a) above and 3.1.1(i), details of inter-state supplies made to unregistered person, composition taxable persons and UIN holders">
        <div className="space-y-4">
          {['Supplies made to Unregistered Persons', 'Supplies made to Composition Taxable Persons', 'Supplies made to UIN holders'].map((heading) => (
            <div key={heading} className="space-y-2">
              <h3 className="text-label-caps uppercase tracking-wider text-on-surface-variant">{heading}</h3>
              <TableShell wide={false}>
                <table className="w-full text-body-md text-left border-collapse">
                  <thead className="border-b border-border bg-muted">
                    <tr>
                      <th className={LABEL_TH}>Place of Supply</th>
                      <th className={SECTION_TH}>Total Taxable value</th>
                      <th className={SECTION_TH}>Amount of Integrated Tax</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr>
                      {Array.from({ length: 3 }, (_, i) => <td key={i} className={`${i === 0 ? 'px-5 py-2.5 text-left' : FIGURE} text-on-surface-subtle`}>{UNSET}</td>)}
                    </tr>
                  </tbody>
                </table>
              </TableShell>
            </div>
          ))}
        </div>
      </ReturnSection>

      {/* ── 4. Eligible ITC ── */}
      <ReturnSection title="4. Eligible ITC">
        <TableShell>
          <table className="w-full min-w-[700px] text-body-md text-left border-collapse">
            <thead className="border-b border-border bg-muted">
              <tr>
                <th className={LABEL_TH}>Details</th>
                <th className={SECTION_TH}>Integrated Tax</th>
                <th className={SECTION_TH}>Central Tax</th>
                <th className={SECTION_TH}>State/UT Tax</th>
                <th className={SECTION_TH}>CESS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <BandRow span={5}>(A) ITC Available</BandRow>
              {[
                '(1) Import of Goods',
                '(2) Import of Services',
                '(3) Inward supplies liable to reverse charge (other than 1 & 2 above)',
                '(4) Inward Supplies form ISD',
                '(5) Any other ITC',
              ].map((item) => (
                <tr key={item} className="hover:bg-muted/50">
                  <td className="py-2.5 pl-10 pr-5 font-medium text-on-surface">{item}</td>
                  {Array.from({ length: 4 }, (_, i) => <td key={i} className={`${FIGURE} text-on-surface-subtle`}>{UNSET}</td>)}
                </tr>
              ))}

              <BandRow span={5}>(B) ITC Reversed</BandRow>
              {[
                '(1) As per rules 38, 42 and 43 of Central Tax Rules and Section 17(5)',
                '(2) Others',
              ].map((item) => (
                <tr key={item} className="hover:bg-muted/50">
                  <td className="py-2.5 pl-10 pr-5 font-medium text-on-surface">{item}</td>
                  {Array.from({ length: 4 }, (_, i) => <td key={i} className={`${FIGURE} text-on-surface-subtle`}>{UNSET}</td>)}
                </tr>
              ))}

              {/* (C) is a derived row on the statutory form — shown as an unset total, not a computed one. */}
              <tr className="border-y border-border-strong bg-muted/60">
                <td className="px-5 py-2.5 font-semibold text-on-surface">(C) Net ITC Available (A) - (B)</td>
                {Array.from({ length: 4 }, (_, i) => <td key={i} className={`${FIGURE} font-semibold text-on-surface-subtle`}>{UNSET}</td>)}
              </tr>

              <BandRow span={5}>(D) Other Details</BandRow>
              {[
                '(1) ITC reclaimed which was reversed under Table 4(B)(2) in earlier tax period',
                '(2) Ineligible ITC under section 16(4) & ITC restricted due to PoS rules',
              ].map((item) => (
                <tr key={item} className="hover:bg-muted/50">
                  <td className="py-2.5 pl-10 pr-5 text-on-surface-variant">{item}</td>
                  {Array.from({ length: 4 }, (_, i) => <td key={i} className={`${FIGURE} text-on-surface-subtle`}>-</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      </ReturnSection>

      {/* ── 5. Exempt, nil-rated and non-GST inward supplies ── */}
      <ReturnSection title="5. Exempt, nil-rated and non-GST inward supplies">
        <TableShell>
          <table className="w-full min-w-[600px] text-body-md text-left border-collapse">
            <thead className="border-b border-border bg-muted">
              <tr>
                <th className={LABEL_TH}>Nature of Supplies</th>
                <th className={SECTION_TH}>Inter-State Supplies</th>
                <th className={SECTION_TH}>Intra-State Supplies</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr className="hover:bg-muted/50">
                <td className="px-5 py-2.5 font-medium text-on-surface">From a supplier under composition scheme, Exempt and Nil rated supply</td>
                <td className={`${FIGURE} text-on-surface-subtle`}>{UNSET}</td>
                <td className={`${FIGURE} text-on-surface-subtle`}>{UNSET}</td>
              </tr>
              <tr className="hover:bg-muted/50">
                <td className="px-5 py-2.5 text-on-surface-variant">Non GST Supply</td>
                <td className={`${FIGURE} text-on-surface-subtle`}>-</td>
                <td className={`${FIGURE} text-on-surface-subtle`}>-</td>
              </tr>
            </tbody>
          </table>
        </TableShell>
      </ReturnSection>

      {/* Reconciliation signpost — no figures, only where the real numbers live. */}
      <p className="rounded-card border border-border bg-muted/40 px-5 py-3 text-body-sm text-on-surface-variant">
        To reconcile a period: take outward tax from GSTR-1 and the Tax Summary, take eligible purchase credit from
        GSTR-2B, then compare the two against what you file on the GST portal. This template is a layout reference
        only and never writes back to the ledger.
      </p>
    </div>
  );
}
